// EditFormFields.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Switch,
  Divider,
  Typography,
  MenuItem,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Autocomplete } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  assignmentModeOptions,
  restPolicyOptions,
} from '../planningPolicyOptions';
import WeeklyScheduleEditor from '../WeeklyScheduleEditor';
import AreaPositionSelector from '../AreaPositionSelector';
import RotationWorkshiftsPicker from '../RotationWorkshiftsPicker';
import LicenseTable from './license/LicenseTable';
import HistoryTable from './HistoryTable';
import EmploymentPeriodsCard from './EmploymentPeriodsCard';

// Estilos derivados del theme (theme.js) para que coincidan siempre con la
// paleta de marca: Azul de Prusia Oscuro #002339 (primary) y Verde Espuma
// #00E875 (secondary). Navy = estructura/jerarquía, verde = estado activo.
const getSectionSx = (theme) => ({
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  borderRadius: 2,
  px: 2,
  py: 2.5,
  backgroundColor: theme.palette.primary.light, // Gris Perla
});

const getSegmentedGroupSx = (theme) => ({
  display: 'inline-flex',
  flexWrap: 'wrap',
  gap: 0.5,
  backgroundColor: theme.palette.primary.light, // Gris Perla
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  borderRadius: 2,
  p: 0.5,
  '& .MuiToggleButtonGroup-grouped': {
    border: '0 !important',
    margin: 0,
  },
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.82rem',
    lineHeight: 1.2,
    color: alpha(theme.palette.primary.main, 0.65),
    backgroundColor: 'transparent',
    borderRadius: 1.5,
    px: 1.5,
    minHeight: 34,
    borderBottom: '2px solid transparent',
    transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
  },
  '& .Mui-selected, & .Mui-selected:hover': {
    color: '#FFFFFF !important',
    backgroundColor: `${theme.palette.primary.main} !important`,
    borderBottom: `2px solid ${theme.palette.secondary.main}`,
  },
});

const getToggleRowSx = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  borderRadius: 1.5,
  pl: 1.25,
  pr: 0.5,
  py: 0.25,
  backgroundColor: '#FFFFFF',
});

const EditFormFields = ({
  formValues,
  fieldLabels,
  handleChange,
  isFormDirty,
  handleSave,
  handleDeactivate,
  employeeData,

  // NUEVO: combos combinados
  comboOptions,
  primaryWAs,
  setPrimaryWAs,
  auxWAs,
  setAuxWAs,
  weeklyScheduleSupported,
  weeklySchedule,
  setWeeklySchedule,
  weeklyScheduleError,
  stationWorkshifts,
  rotationWorkshiftSupported,
  rotationWorkshifts,
  setRotationWorkshifts,
  canEditPersonalData = false,
  canEditAssignments = false,

  // NUEVO: props para activar/desactivar
  isEmployeeInactive,
  onActivateEmployee,
  activating,
  saving = false,

  // Inasistencias / Historial (pestañas)
  licenses,
  history,
  employmentPeriods,
  onRefresh,
}) => {
  const theme = useTheme();
  const sectionSx = getSectionSx(theme);
  const segmentedGroupSx = getSegmentedGroupSx(theme);
  const toggleRowSx = getToggleRowSx(theme);

  const [firstDayError, setFirstDayError] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // 👇 CAMBIO: Usamos directamente isEmployeeInactive de las props
  const isDisabled = isEmployeeInactive;
  const isBusy = isDisabled || activating || saving;
  const dataFieldsDisabled = isBusy || !canEditPersonalData;
  const assignmentFieldsDisabled = isBusy || !canEditAssignments;

  useEffect(() => {
    if (!formValues.first_day && employeeData?.data_form?.[0]?.first_day?.value) {
      const rawValue = employeeData.data_form[0].first_day.value || '';
      handleChange({
        target: {
          name: 'first_day',
          value: String(rawValue).split('T')[0],
        },
      });
    }
  }, [employeeData, formValues.first_day, handleChange]);

  const handleDateChange = (date) => {
    if (date) {
      handleChange({
        target: {
          name: 'first_day',
          value: date.toISOString().split('T')[0],
        },
      });
      setFirstDayError(false);
    } else {
      handleChange({ target: { name: 'first_day', value: '' } });
      setFirstDayError(true);
    }
  };

  const parseDate = (d) => (d && !isNaN(Date.parse(d)) ? new Date(`${d}T00:00:00`) : null);

  const stationsOptions = useMemo(
    () => employeeData?.data_form?.[0]?.possible_station || [],
    [employeeData]
  );
  const availableAssignmentModes = useMemo(
    () => assignmentModeOptions.filter((option) => {
      if (option.value === 'fixed_schedule') return weeklyScheduleSupported;
      if (option.value === 'restricted_rotation') return rotationWorkshiftSupported;
      return true;
    }),
    [weeklyScheduleSupported, rotationWorkshiftSupported]
  );

  const selectedStation = useMemo(() => {
    const id = Number(formValues.station || 0);
    return stationsOptions.find((o) => Number(o.id) === id) || null;
  }, [stationsOptions, formValues.station]);

  const renderToggleRow = (name, label, helperText) => (
    <Box sx={toggleRowSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: theme.palette.primary.main,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </Typography>
        <Tooltip title={helperText} arrow placement="top">
          <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
        </Tooltip>
      </Box>
      <Switch
        size="small"
        color="secondary"
        name={name}
        checked={formValues[name] === '1'}
        onChange={handleChange}
        disabled={assignmentFieldsDisabled}
      />
    </Box>
  );

  const renderSectionTitle = (text, sx) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, ...sx }}>
      <Box
        sx={{
          width: 4,
          height: 16,
          borderRadius: 4,
          backgroundColor: theme.palette.primary.main,
          flexShrink: 0,
        }}
      />
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
        {text}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 4 }}>
      <Box sx={sectionSx}>
        {renderSectionTitle('Datos personales', { mb: 0.5 })}
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Información básica del empleado, fecha de ingreso y estación.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' },
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            label={fieldLabels.ci}
            name="ci"
            value={formValues.ci}
            InputProps={{
              readOnly: true,
              sx: { backgroundColor: '#E0E0E0' },
            }}
          />

          <DatePicker
            label={fieldLabels.first_day}
            inputFormat="dd-MM-yyyy"
            value={parseDate(formValues.first_day)}
            onChange={handleDateChange}
            disabled={dataFieldsDisabled}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                error={firstDayError}
                helperText={firstDayError && 'Por favor selecciona una fecha válida'}
              />
            )}
          />

          <TextField
            fullWidth
            label={fieldLabels.name}
            name="name"
            value={formValues.name}
            onChange={handleChange}
            disabled={dataFieldsDisabled}
          />

          <TextField
            fullWidth
            label={fieldLabels.surname}
            name="surname"
            value={formValues.surname}
            onChange={handleChange}
            disabled={dataFieldsDisabled}
          />

          {stationsOptions.length > 0 ? (
            <Autocomplete
              options={stationsOptions}
              getOptionLabel={(opt) => opt?.name ?? ''}
              isOptionEqualToValue={(opt, val) => Number(opt.id) === Number(val.id)}
              value={selectedStation}
              onChange={(_, newVal) => {
                handleChange({
                  target: { name: 'station', value: newVal ? String(newVal.id) : '' },
                });
              }}
              disabled={assignmentFieldsDisabled}
              renderInput={(params) => <TextField {...params} label={fieldLabels.station} />}
            />
          ) : (
            <TextField
              fullWidth
              label={fieldLabels.station}
              value={employeeData?.data_form?.[0]?.station?.value || ''}
              InputProps={{ readOnly: true, sx: { backgroundColor: '#F5F5F5' } }}
              helperText="No hay estaciones disponibles para seleccionar"
            />
          )}
        </Box>
      </Box>

      <Box>
        <Tabs
          value={activeTab}
          onChange={(_, next) => setActiveTab(next)}
          variant="fullWidth"
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            mb: 2.5,
            minHeight: 'auto',
            backgroundColor: theme.palette.primary.light,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            borderRadius: 2,
            p: 0.5,
            '& .MuiTabs-flexContainer': {
              gap: 0.5,
            },
            '& .MuiTab-root': {
              minHeight: 42,
              borderRadius: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.9rem',
              color: alpha(theme.palette.primary.main, 0.65),
              backgroundColor: 'transparent',
              borderBottom: '2px solid transparent',
              transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
            },
            '& .Mui-selected': {
              color: '#FFFFFF !important',
              backgroundColor: theme.palette.primary.main,
              borderBottom: `2px solid ${theme.palette.secondary.main}`,
            },
          }}
        >
          <Tab label="General" />
          <Tab label="Inasistencias" />
          <Tab label="Historial" />
        </Tabs>

        {activeTab === 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2.5,
              alignItems: 'start',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={sectionSx}>
                {renderSectionTitle('Permisos operativos')}

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1,
                  }}
                >
                  {renderToggleRow(
                    'is_manager',
                    fieldLabels.is_manager,
                    'Queda marcado como encargado principal de su operación.'
                  )}
                  {renderToggleRow(
                    'override_manager',
                    fieldLabels.override_manager,
                    'Puede cubrir funciones de encargado cuando haga falta.'
                  )}
                  {renderToggleRow(
                    'is_night',
                    fieldLabels.is_night,
                    'Participa en la rotación estructural de turnos nocturnos.'
                  )}
                  {renderToggleRow(
                    'override_night',
                    fieldLabels.override_night,
                    'Puede tomar turnos nocturnos manuales para coberturas puntuales.'
                  )}
                </Box>
              </Box>

              <EmploymentPeriodsCard employmentPeriods={employmentPeriods} />

              <Box sx={sectionSx}>
                {renderSectionTitle('Asignación')}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <AreaPositionSelector
                    multiple
                    options={comboOptions || []}
                    value={primaryWAs || []}
                    onChange={(value) => {
                      const nextPrimaryIds = new Set((value || []).map((o) => o.id));
                      setPrimaryWAs(value || []);
                      setAuxWAs((auxWAs || []).filter((o) => !nextPrimaryIds.has(o.id)));
                    }}
                    disabled={assignmentFieldsDisabled}
                  />

                  <AreaPositionSelector
                    multiple
                    options={comboOptions || []}
                    value={auxWAs || []}
                    excludeAreaIds={primaryWAs?.[0]?.positionId != null ? [primaryWAs[0].positionId] : []}
                    onChange={(value) => setAuxWAs(value || [])}
                    disabled={assignmentFieldsDisabled}
                    areaLabel="Área auxiliar"
                    positionLabel="Posición auxiliar"
                  />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, maxWidth: { sm: '60%' } }}>
                    <DatePicker
                      label={fieldLabels.aux_valid_from || 'Vigencia roles auxiliares'}
                      inputFormat="dd-MM-yyyy"
                      value={parseDate(formValues.aux_valid_from)}
                      onChange={(date) => {
                        handleChange({
                          target: {
                            name: 'aux_valid_from',
                            value: date ? date.toISOString().split('T')[0] : '',
                          },
                        });
                      }}
                      disabled={assignmentFieldsDisabled}
                      renderInput={(params) => (
                        <TextField {...params} size="small" fullWidth />
                      )}
                    />
                    <Tooltip
                      title="Los cambios en roles auxiliares se aplican desde esta fecha en calendarios abiertos."
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 17, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                    </Tooltip>
                  </Box>

                  <TextField
                    fullWidth
                    select
                    label={fieldLabels.rest_policy}
                    name="rest_policy"
                    value={formValues.rest_policy}
                    onChange={handleChange}
                    disabled={assignmentFieldsDisabled}
                  >
                    {restPolicyOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
            </Box>

            <Box sx={sectionSx}>
              {renderSectionTitle('Modalidad de asignación de turno')}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <ToggleButtonGroup
                    value={formValues.assignment_mode}
                    exclusive
                    onChange={(_, next) => {
                      if (next) handleChange({ target: { name: 'assignment_mode', value: next } });
                    }}
                    disabled={assignmentFieldsDisabled}
                    sx={segmentedGroupSx}
                  >
                    {availableAssignmentModes.map((option) => (
                      <ToggleButton key={option.value} value={option.value}>
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {(() => {
                    const modeCaption =
                      formValues.assignment_mode === 'fixed_schedule' && weeklyScheduleError
                        ? weeklyScheduleError
                        : availableAssignmentModes.find((o) => o.value === formValues.assignment_mode)?.description;
                    return modeCaption ? (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 1,
                          color:
                            weeklyScheduleError && formValues.assignment_mode === 'fixed_schedule'
                              ? 'error.main'
                              : 'text.secondary',
                        }}
                      >
                        {modeCaption}
                      </Typography>
                    ) : null;
                  })()}
                </Box>

                {formValues.assignment_mode === 'fixed_schedule' && (
                  <WeeklyScheduleEditor
                    value={weeklySchedule}
                    onChange={setWeeklySchedule}
                    workshiftOptions={stationWorkshifts}
                    disabled={assignmentFieldsDisabled}
                    error={weeklyScheduleError}
                    helperText={
                      stationWorkshifts.length === 0
                        ? 'Los turnos disponibles se cargan según la estación seleccionada.'
                        : 'Usá Libre para descansos fijos como domingos o días no laborables recurrentes.'
                    }
                  />
                )}

                {formValues.assignment_mode === 'restricted_rotation' && (
                  <RotationWorkshiftsPicker
                    workshifts={stationWorkshifts}
                    value={rotationWorkshifts}
                    onChange={setRotationWorkshifts}
                    disabled={assignmentFieldsDisabled}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <LicenseTable
            licenses={licenses}
            fetchLicenses={onRefresh}
            ci={formValues.ci}
            isEmployeeInactive={isEmployeeInactive}
            canManageLicenses={canEditAssignments}
          />
        )}

        {activeTab === 2 && <HistoryTable history={history} />}
      </Box>

      <Divider flexItem />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        {/* Botón dinámico: Activar / Desactivar */}
        {isEmployeeInactive ? (
          canEditPersonalData ? (
            <Button
              variant="contained"
              color="secondary"
              onClick={onActivateEmployee}
              disabled={activating || saving}
              startIcon={activating ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
            >
              {activating ? 'Activando...' : 'Activar'}
            </Button>
          ) : <Box />
        ) : (
          canEditPersonalData ? (
            <Button
              variant="contained"
              color="error"
              onClick={handleDeactivate}
              disabled={saving}
              startIcon={<CancelIcon />}
            >
              Desactivar
            </Button>
          ) : <Box />
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={!isFormDirty || isBusy || (!canEditPersonalData && !canEditAssignments)}
        >
          {employeeData?.button_save?.text || 'Guardar'}
        </Button>
      </Box>
    </Box>
  );
};

export default EditFormFields;
